import React, { JSX, useEffect, useState } from 'react'
import { Link } from 'react-router'
import {
  InformationCircleIcon,
  StopCircleIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { YOUTUBE_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { YouTubeAreaIconLink } from 'components/YouTubeAreaIconLink'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { YOUTUBE_TERMS_URL } from 'lib/externalLinks'
import {
  httpsImageOrNull,
  useAccountYouTubeSessions,
  useEventYouTubeSessions,
  YouTubeSessionsStore
} from 'hooks/useYouTubeSessions'
import {
  Connection,
  YouTubeChatSession,
  YouTubeChatSessionResult
} from 'types/api'
import listMyConnectionsQuery from 'graphql/listMyConnections.graphql'
import startYouTubeChatSessionMutation from 'graphql/startYouTubeChatSession.graphql'
import stopYouTubeChatSessionMutation from 'graphql/stopYouTubeChatSession.graphql'

export type YouTubeSessionWidgetScope =
  | { kind: 'account' }
  | { kind: 'event', eventId: string }

export const YT_CONNECT_REQUIRED_PREFIX = 'Connect your YouTube account'
export const YT_CONSENT_REQUIRED_PREFIX = 'Review and agree to the YouTube terms'

export const SESSION_REFUSAL_COPY: Record<string, string> = {
  NOT_LIVE: 'No active live broadcast was found. Make sure the stream is live, then try again.',
  CEILING_REACHED: 'FlightDeck\'s YouTube chat capacity is full right now. Try again in a few minutes.',
  KILL_SWITCH_ACTIVE: 'FlightDeck has paused YouTube chat for maintenance. Nothing is wrong with your connection. Try again later.',
  SESSION_ALREADY_RUNNING: 'A YouTube chat session is already running for that channel.',
  ACCOUNT_SESSION_CAP: 'Your account is at FlightDeck\'s limit for YouTube chats running at once. Stop one, then try again.',
  CHAT_NOT_ACCESSIBLE: 'That live chat is not publicly readable. Members-only and private chats cannot be read; public live streams with an open chat work.',
  CHANNEL_IN_USE: 'That YouTube channel already feeds another stream. Stop it there first.'
}

export const YOUTUBE_SESSION_ENDED = 'Last session ended.'

export const useYouTubeConnectionIdentity = (): {
  connected: boolean
  channelId: string | null
  channelTitle: string | null
} => {
  const { auth0Token } = useAuth()
  const [identity, setIdentity] = useState<{
    connected: boolean
    channelId: string | null
    channelTitle: string | null
  }>({ connected: false, channelId: null, channelTitle: null })
  useEffect(() => {
    if (auth0Token === undefined) return
    let cancelled = false
    const load = async (): Promise<void> => {
      try {
        const resp = await graphqlRequest<{ connections: Connection[] }>(
          listMyConnectionsQuery, {}, auth0Token
        )
        if (cancelled) return
        const youtube = (resp.connections ?? []).find(
          (c) => c.provider === 'youtube' && c.status === 'connected'
        ) ?? null
        setIdentity({
          connected: youtube !== null,
          channelId: youtube?.youtubeChannelId ?? null,
          channelTitle: youtube?.displayName ?? youtube?.login ?? null
        })
      } catch {
      }
    }
    void load()
    return () => { cancelled = true }
  }, [auth0Token])
  return identity
}

export const YouTubeTermsFooter = (): JSX.Element => (
  <p className='mt-4 border-t border-white/5 pt-3 text-[11px] text-dm-text-accent'>
    YouTube API Services are subject to the{' '}
    <a
      href={YOUTUBE_TERMS_URL}
      target='_blank'
      rel='noreferrer'
      className='font-medium text-brand underline hover:text-brand/80'
    >
      YouTube Terms of Service
    </a>.
  </p>
)

const WidgetError = ({
  message,
  onDismiss
}: {
  message: string
  onDismiss: () => void
}): JSX.Element => {
  const linked = message.startsWith(YT_CONNECT_REQUIRED_PREFIX) ||
    message.startsWith(YT_CONSENT_REQUIRED_PREFIX)
  if (linked) {
    return (
      <ErrorInline onDismiss={onDismiss}>
        {message}{' '}
        <Link to='/connections' className='font-semibold text-red-200 underline hover:text-red-100'>
          Open Connections
        </Link>
      </ErrorInline>
    )
  }
  return <ErrorInline onDismiss={onDismiss}>{message}</ErrorInline>
}

export const HOST_ONLY_WIDGET_NOTE = 'Only the host can start or stop the host\'s YouTube chat.'
export const OWN_YOUTUBE_START_LABEL = 'Start my own YouTube chat'
export const OWN_YOUTUBE_START_NOTE = 'This reads your own YouTube channel, not the host\'s.'
const HOST_YOUTUBE_START_LABEL = 'Start YouTube chat'

export interface EventSessionRows {
  isOwn: (r: YouTubeChatSession) => boolean
  ownRow: YouTubeChatSession | null
  hostRows: YouTubeChatSession[]
  othersLive: number
  startLabel: string
}

export const eventSessionRowsFor = ({
  rows,
  isHost,
  ownChannelId
}: {
  rows: readonly YouTubeChatSession[]
  isHost: boolean
  ownChannelId: string | null
}): EventSessionRows => {
  const isOwn = (r: YouTubeChatSession): boolean =>
    !(!isHost && r.hostSession === true) &&
    (r.channelId == null || r.channelId === '' || r.channelId === ownChannelId)
  return {
    isOwn,
    ownRow: rows.find(isOwn) ?? null,
    hostRows: isHost ? [] : rows.filter((r) => r.hostSession === true),
    othersLive: rows.filter((r) => r.status === 'RUNNING' && !isOwn(r)).length,
    startLabel: isHost ? HOST_YOUTUBE_START_LABEL : OWN_YOUTUBE_START_LABEL
  }
}

const IDLE_COPY_EVENT = 'Start a YouTube chat session while your stream is live. We read your live chat through the official YouTube Data API, only while the session runs.'
const IDLE_COPY_ACCOUNT = 'Start a YouTube chat session for your own channel here, or add any public channel in a chat overlay editor\'s Sources card. We read live chat through the official YouTube Data API, only while a session runs, and each session feeds the chat overlays that list its channel.'

const conflictCopy = (
  session: YouTubeChatSession,
  scope: YouTubeSessionWidgetScope
): string => {
  if (scope.kind === 'event') {
    if (session.scope === 'ACCOUNT') {
      return 'A YouTube session is already running for Quick Overlays only. Stop it, then start it for this stream so this stream\'s overlays show it and votes count.'
    }
    return 'A YouTube session is already running for another stream. Stop it, then start it for this stream so votes count.'
  }
  return 'A YouTube session is already running for a stream. Stop it, then start it account-wide if you want it to outlive the stream.'
}

const compareIso = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const compareRows = (a: YouTubeChatSession, b: YouTubeChatSession): number => {
  if ((a.status === 'RUNNING') !== (b.status === 'RUNNING')) {
    return a.status === 'RUNNING' ? -1 : 1
  }
  return compareIso(b.startedAt, a.startedAt)
}

const pictureOf = (store: YouTubeSessionsStore, row: YouTubeChatSession): string | null => {
  const id = row.channelId
  const cached = typeof id === 'string' && id !== '' ? store.meta[id]?.imageUrl ?? null : null
  return cached ?? httpsImageOrNull(row.channelProfileImageUrl)
}

const ChannelPicture = ({ src }: { src: string | null }): JSX.Element | null => {
  if (src === null) return null
  return (
    <img
      src={src}
      alt=''
      referrerPolicy='no-referrer'
      onError={(e) => { e.currentTarget.style.display = 'none' }}
      className='size-5 shrink-0 rounded object-cover'
      data-testid='youtube-session-channel-picture'
    />
  )
}

const stopVariablesFor = (
  row: YouTubeChatSession,
  eventId: string | null
): { eventId?: string, channelId?: string } => {
  const channelId = typeof row.channelId === 'string' && row.channelId !== '' ? row.channelId : undefined
  return {
    ...(eventId !== null && { eventId }),
    ...(channelId !== undefined && { channelId })
  }
}

export const YouTubeSessionWidget = ({
  scope,
  variant,
  sessionsStore,
  ownSourceAttached = true,
  idleCopy = IDLE_COPY_ACCOUNT,
  isHost = true
}: {
  scope: YouTubeSessionWidgetScope
  variant: 'card' | 'compact'
  sessionsStore?: YouTubeSessionsStore
  ownSourceAttached?: boolean
  idleCopy?: string
  isHost?: boolean
}): JSX.Element | null => {
  const { auth0Token } = useAuth()
  const { account, status: entStatus } = useEntitlements()
  const invited = account?.youtubeChatInvited === true
  const consented = typeof account?.youtubeConsentAt === 'string' && account.youtubeConsentAt.length > 0
  const entReady = entStatus === 'ready' && account !== null

  const {
    connected: hasYouTube,
    channelId: ownChannelId,
    channelTitle: ownChannelTitle
  } = useYouTubeConnectionIdentity()

  const gateOk = entReady && invited && consented && hasYouTube
  const ownAccountStore = useAccountYouTubeSessions(
    scope.kind === 'account' && sessionsStore === undefined && gateOk
  )
  const eventStore = useEventYouTubeSessions(
    scope.kind === 'event' ? scope.eventId : '',
    scope.kind === 'event' && entReady && invited && (gateOk || !ownSourceAttached)
  )
  const store: YouTubeSessionsStore = scope.kind === 'account'
    ? (sessionsStore ?? ownAccountStore)
    : eventStore
  const eventId = scope.kind === 'event' ? scope.eventId : null

  const liveCap = account?.youtubeSessionsPerAccount ?? null
  const atLiveCap = scope.kind === 'account' && liveCap !== null &&
    store.runningCount >= liveCap
  const [busy, setBusy] = useState<'starting' | 'stopping' | null>(null)
  const [stoppingKey, setStoppingKey] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<{ code: string, message: string | null } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<YouTubeChatSession | null>(null)

  useEffect(() => {
    if (conflict === null) return
    const listed = store.sessions.find((r) => r.sessionId === conflict.sessionId)
    if (listed !== undefined && listed.status !== 'RUNNING') setConflict(null)
  }, [conflict, store.sessions])

  const rows = [...store.sessions].sort(compareRows)
  const hostView = scope.kind !== 'event' || isHost
  const { ownRow, hostRows, othersLive, startLabel } = eventSessionRowsFor({ rows, isHost: hostView, ownChannelId })
  const ownRunning = ownRow !== null && ownRow.status === 'RUNNING'
  const hostOnlyNoteShown = hostRows.length > 0
  const keyOf = (r: YouTubeChatSession): string => r.channelId ?? r.sessionId

  const handleStart = async (): Promise<void> => {
    if (auth0Token === undefined || busy !== null || atLiveCap) return
    setBusy('starting')
    setError(null)
    setRefusal(null)
    try {
      const variables = eventId !== null ? { eventId } : {}
      const { result } = await graphqlRequest<{ result: YouTubeChatSessionResult }>(
        startYouTubeChatSessionMutation, variables, auth0Token
      )
      const session = result.session
      if (session != null) {
        const belongsHere = eventId === null || (session.scope === 'EVENT' && session.eventId === eventId)
        if (belongsHere) {
          store.upsert(session)
          setConflict(null)
        } else if (session.status === 'RUNNING') {
          setConflict(session)
        }
      }
      if (!result.started && result.refusalCode !== null) {
        setRefusal({ code: result.refusalCode, message: result.message })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start YouTube chat')
    } finally {
      setBusy(null)
    }
  }

  const handleStopRow = async (row: YouTubeChatSession): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    setBusy('stopping')
    setStoppingKey(keyOf(row))
    setError(null)
    setRefusal(null)
    try {
      const resp = await graphqlRequest<{ session: YouTubeChatSession | null }>(
        stopYouTubeChatSessionMutation, stopVariablesFor(row, eventId), auth0Token
      )
      if (resp.session != null) store.upsert(resp.session)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop YouTube chat')
    } finally {
      setBusy(null)
      setStoppingKey(null)
    }
  }

  const handleStopConflict = async (): Promise<void> => {
    if (auth0Token === undefined || busy !== null || conflict === null) return
    setBusy('stopping')
    setStoppingKey(null)
    setError(null)
    setRefusal(null)
    try {
      const resp = await graphqlRequest<{ session: YouTubeChatSession | null }>(
        stopYouTubeChatSessionMutation, stopVariablesFor(conflict, null), auth0Token
      )
      const stopped = resp.session
      if (stopped != null && eventId === null) store.upsert(stopped)
      setConflict(stopped != null && stopped.status === 'RUNNING' ? stopped : null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop YouTube chat')
    } finally {
      setBusy(null)
    }
  }

  if (!entReady) {
    if (variant === 'compact') return null
    return (
      <div data-testid='youtube-session-widget' className='flex items-center justify-center rounded-lg border border-white/5 bg-dm-bg-accent/20 py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (!invited) return null

  const needsConnections = !consented || !hasYouTube
  const conflictRunning = conflict !== null && conflict.status === 'RUNNING' && !ownRunning
  const moreLive = othersLive > 0
    ? (
      <span className='shrink-0 text-[11px] text-dm-text-accent' data-testid='yt-widget-more-live'>
        +{othersLive} more live
      </span>
      )
    : null

  if (variant === 'compact') {
    let compactBody: JSX.Element
    if (needsConnections && ownSourceAttached) {
      compactBody = (
        <Link to='/connections' className='text-[11px] font-medium text-brand underline hover:text-brand/80'>
          Connect YouTube
        </Link>
      )
    } else if (ownRunning && ownRow !== null) {
      compactBody = (
        <>
          <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400' aria-hidden='true' />
          <span className='text-[11px] text-emerald-300'>Live, reading YouTube chat</span>
          <button
            type='button'
            onClick={() => { void handleStopRow(ownRow) }}
            disabled={busy !== null}
            data-testid='yt-widget-compact-stop'
            className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'stopping' ? <LoadingSpinner /> : <StopCircleIcon className='size-3.5' aria-hidden='true' />}
            Stop
          </button>
          {moreLive}
        </>
      )
    } else if (conflictRunning && conflict !== null) {
      compactBody = (
        <>
          <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400' aria-hidden='true' />
          <span className='max-w-56 truncate text-[11px] text-amber-300' title={conflictCopy(conflict, scope)}>
            YouTube session running elsewhere
          </span>
          <button
            type='button'
            onClick={() => { void handleStopConflict() }}
            disabled={busy !== null}
            data-testid='yt-widget-compact-stop-conflict'
            className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'stopping' ? <LoadingSpinner /> : <StopCircleIcon className='size-3.5' aria-hidden='true' />}
            Stop
          </button>
        </>
      )
    } else if (ownSourceAttached) {
      compactBody = (
        <>
          <button
            type='button'
            onClick={() => { void handleStart() }}
            disabled={busy !== null}
            data-testid='yt-widget-compact-start'
            title={hostView ? undefined : OWN_YOUTUBE_START_NOTE}
            className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'starting' ? <LoadingSpinner /> : <img src={YOUTUBE_BRAND_ICON_SRC} alt='' className='h-3.5 w-auto' aria-hidden='true' />}
            {startLabel}
            {!hostView && <span className='sr-only'> {OWN_YOUTUBE_START_NOTE}</span>}
          </button>
          {moreLive}
        </>
      )
    } else {
      compactBody = (
        <span className='text-[11px] text-dm-text-secondary' data-testid='yt-widget-compact-status'>
          {othersLive > 0
            ? `${othersLive} YouTube ${othersLive === 1 ? 'chat' : 'chats'} live`
            : 'YouTube chat not running'}
        </span>
      )
    }
    return (
      <div
        data-testid='youtube-session-widget-compact'
        className='flex items-center gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 px-2.5 py-1.5'
      >
        <YouTubeAreaIconLink size='sm' />
        {compactBody}

        {hostOnlyNoteShown && (
          <span className='inline-flex shrink-0 items-center' data-testid='yt-widget-host-only-note' title={HOST_ONLY_WIDGET_NOTE}>
            <InformationCircleIcon className='size-4 text-dm-text-accent' aria-hidden='true' />
            <span className='sr-only'>{HOST_ONLY_WIDGET_NOTE}</span>
          </span>
        )}
        {refusal !== null && (
          <InformationCircleIcon
            className='size-4 shrink-0 text-brand'
            title={SESSION_REFUSAL_COPY[refusal.code] ?? refusal.message ?? 'YouTube chat could not start.'}
          />
        )}
      </div>
    )
  }

  let body: JSX.Element
  if (needsConnections) {
    body = (
      <div>
        <p className='text-sm text-dm-text-secondary'>
          Your account has YouTube preview access. Review the YouTube terms
          and connect your channel on the Connections page, then come back
          here to start reading your live chat.
        </p>
        <Link
          to='/connections'
          className='mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand/80'
        >
          Go to Connections
        </Link>
      </div>
    )
  } else if (scope.kind === 'account') {
    body = (
      <div>
        {rows.length === 0 && (
          <p className='text-sm text-dm-text-secondary'>
            Not running. {idleCopy}
          </p>
        )}
        {rows.length > 0 && (
          <ul className='space-y-2' data-testid='youtube-session-list'>
            {rows.map((row) => {
              const rowRunning = row.status === 'RUNNING'
              const rowBusy = busy === 'stopping' && stoppingKey === keyOf(row)
              const metaTitle = typeof row.channelId === 'string' ? store.meta[row.channelId]?.title ?? null : null
              return (
                <li
                  key={keyOf(row)}
                  className='rounded-md border border-white/10 bg-dm-bg-accent/30 p-3'
                >
                  <div className='flex items-center gap-2'>
                    <span
                      className={rowRunning
                        ? 'h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400'
                        : 'h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-500'}
                      aria-hidden='true'
                    />
                    <ChannelPicture src={pictureOf(store, row)} />
                    <span className='min-w-0 flex-1 truncate text-xs font-medium text-dm-text-primary'>
                      {metaTitle ?? row.channelTitle ?? row.channelId ?? ownChannelTitle ?? 'Your channel'}
                      {row.scope === 'EVENT' && (
                        <span className='ml-1.5 text-[10px] font-normal text-dm-text-accent'>
                          stream session
                        </span>
                      )}
                    </span>
                    {rowRunning && (
                      <button
                        type='button'
                        onClick={() => { void handleStopRow(row) }}
                        disabled={busy !== null}
                        data-testid='stop-youtube-chat-row-button'
                        className='inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                      >
                        {rowBusy ? <LoadingSpinner /> : <StopCircleIcon className='size-3.5' aria-hidden='true' />}
                        Stop
                      </button>
                    )}
                  </div>
                  <p
                    className={rowRunning
                      ? 'mt-1 text-[11px] text-emerald-300'
                      : 'mt-1 text-[11px] text-dm-text-secondary'}
                    data-testid={rowRunning ? undefined : 'youtube-session-row-ended'}
                  >
                    {rowRunning
                      ? <>Live, reading chat through the official YouTube Data API. Started {formatLocalDateTime(row.startedAt)}.</>
                      : YOUTUBE_SESSION_ENDED}
                  </p>
                </li>
              )
            })}
          </ul>
        )}
        {!ownRunning && atLiveCap && liveCap !== null && (
          <p className='mt-3 text-[11px] font-medium text-dm-text-secondary' data-testid='youtube-session-live-cap-note'>
            {liveCap === 0
              ? 'FlightDeck has paused new YouTube live chats right now.'
              : `FlightDeck allows ${liveCap === 1 ? 'one YouTube live chat' : `${liveCap} YouTube live chats`} at a time on your account. Stop one to start another.`}
          </p>
        )}
        {!ownRunning && (
          <button
            type='button'
            onClick={() => { void handleStart() }}
            disabled={busy !== null || atLiveCap}
            data-testid='connect-youtube-chat-button'
            className='mt-4 inline-flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-sm font-semibold text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'starting' ? <LoadingSpinner /> : <img src={YOUTUBE_BRAND_ICON_SRC} alt='' className='h-4 w-auto' aria-hidden='true' />}
            Start YouTube chat
          </button>
        )}
      </div>
    )
  } else if (ownRunning && ownRow !== null) {
    body = (
      <div>
        <div
          data-testid='youtube-session-live'
          className='flex items-start gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-300'
        >
          <span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400' aria-hidden='true' />
          <span>
            Live, reading YouTube chat for this stream through the
            official YouTube Data API. Started {formatLocalDateTime(ownRow.startedAt)}.
          </span>
        </div>
        {moreLive !== null && <p className='mt-2'>{moreLive}</p>}
        <button
          type='button'
          onClick={() => { void handleStopRow(ownRow) }}
          disabled={busy !== null}
          data-testid='stop-youtube-chat-button'
          className='mt-3 inline-flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-sm font-semibold text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busy === 'stopping' ? <LoadingSpinner /> : null}
          Stop YouTube chat
        </button>
      </div>
    )
  } else if (conflictRunning && conflict !== null) {
    body = (
      <div>
        <div
          data-testid='youtube-session-conflict'
          className='flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300'
        >
          <InformationCircleIcon className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
          <span>{conflictCopy(conflict, scope)}</span>
        </div>
        <button
          type='button'
          onClick={() => { void handleStopConflict() }}
          disabled={busy !== null}
          data-testid='stop-youtube-chat-conflict-button'
          className='mt-3 inline-flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-sm font-semibold text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busy === 'stopping' ? <LoadingSpinner /> : <StopCircleIcon className='size-4' aria-hidden='true' />}
          Stop the running session
        </button>
      </div>
    )
  } else {
    body = (
      <div>
        <p className='text-sm text-dm-text-secondary'>
          Not running.{' '}
          {IDLE_COPY_EVENT}
        </p>
        {moreLive !== null && <p className='mt-2'>{moreLive}</p>}
        {ownRow !== null && ownRow.status === 'STOPPED' && (
          <div
            data-testid='youtube-session-stopped'
            className='mt-3 flex items-start gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-xs text-dm-text-secondary'
          >
            <InformationCircleIcon className='mt-0.5 size-4 shrink-0 text-brand' aria-hidden='true' />
            <span>{YOUTUBE_SESSION_ENDED}</span>
          </div>
        )}
        {ownSourceAttached && (
          <button
            type='button'
            onClick={() => { void handleStart() }}
            disabled={busy !== null}
            data-testid='connect-youtube-chat-button'
            className='mt-4 inline-flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-sm font-semibold text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'starting' ? <LoadingSpinner /> : <img src={YOUTUBE_BRAND_ICON_SRC} alt='' className='h-4 w-auto' aria-hidden='true' />}
            {startLabel}
          </button>
        )}
        {ownSourceAttached && !hostView && (
          <p className='mt-2 text-[11px] text-dm-text-accent' data-testid='yt-widget-own-start-note'>
            {OWN_YOUTUBE_START_NOTE}
          </p>
        )}
      </div>
    )
  }

  return (
    <div
      data-testid='youtube-session-widget'
      className='rounded-lg border border-white/5 bg-dm-bg-accent/20 p-4'
    >
      <div className='mb-3 flex items-center gap-2'>
        <YouTubeAreaIconLink size='sm' />
        <h4 className='text-sm font-semibold text-dm-text-primary'>
          {scope.kind === 'account' ? 'YouTube chat sessions' : 'YouTube chat session'}
        </h4>
        {ownChannelTitle !== null && (
          <span className='min-w-0 truncate text-[11px] text-dm-text-accent'>
            {ownChannelTitle}
          </span>
        )}
      </div>
      {body}
      {hostOnlyNoteShown && (
        <p className='mt-3 text-[11px] text-dm-text-accent' data-testid='yt-widget-host-only-note'>
          {HOST_ONLY_WIDGET_NOTE}
        </p>
      )}
      {refusal !== null && (
        <div
          data-testid='youtube-session-refusal'
          className='mt-3 flex items-start gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-xs text-dm-text-secondary'
        >
          <InformationCircleIcon className='mt-0.5 size-4 shrink-0 text-brand' aria-hidden='true' />
          <span>
            {SESSION_REFUSAL_COPY[refusal.code] ?? refusal.message ?? 'YouTube chat could not start. Try again in a moment.'}
          </span>
        </div>
      )}
      {error !== null && (
        <div className='mt-3'>
          <WidgetError message={error} onDismiss={() => setError(null)} />
        </div>
      )}
    </div>
  )
}
